import { useMemo, useRef } from "react";
import useSWR from "swr";
import { configureUrlWithParams } from "../utils";
import offThreadQueryResponse from "../middleware/offThreadQueryResponse";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";

/**
 * Hook for fetching dashboard ticker data independently
 * This enables parallel loading of tickers via the shared clickhouse_ticker_query
 *
 * NOTE: Tickers are "timeless" - they use today() in the query and don't depend
 * on the user-selected date range. The endpoint only depends on locations.
 *
 * Usage:
 *   const { tickerData, isLoading, error, reload } = useFetchTickerData({
 *     dashboard: 'sales',
 *     filters,
 *     shouldFetch: true
 *   });
 */
const useFetchTickerData = ({
  dashboard,
  filters,
  shouldFetch,
}: {
  dashboard: string;
  filters: any;
  shouldFetch: boolean;
}) => {
  const { company, dashboard: dashboardState } = useSelector((state: RootState) => state);
  const timezoneOffset = company?.company_details?.time_zone_offset;
  const enableClickhouse = (company?.company_details as any)?.enable_clickhouse;

  // Check if the current dashboard actually supports independent ticker queries.
  // This is populated from the dashboard list API on app startup, so it's available
  // before any dashboard page is visited — no timing issue.
  const dashboardHasTickerQuery = useMemo(() => {
    if (!dashboard) return false;
    const dashboardMeta = dashboardState?.dashboards?.find(
      (d) => d.api_path === dashboard
    );
    return dashboardMeta?.has_ticker_query ?? false;
  }, [dashboard, dashboardState?.dashboards]);

  // Store the initial filters for tickers (so we don't refetch on date change)
  // Tickers are timeless - they use today() in the query and don't need user-selected dates
  const initialFiltersRef = useRef({
    dates: filters.dates,
    timeRange: filters.timeRange,
  });

  // Build the endpoint URL
  // NOTE: Tickers are timeless - they don't depend on selected date range
  // We only depend on locations, so changing dates won't trigger refetch
  const endpoint = useMemo(() => {
    // Only fetch if ClickHouse is enabled AND this dashboard has a ticker query
    if (!enableClickhouse || !dashboardHasTickerQuery) {
      return null;
    }

    if (dashboard && filters.locations.length > 0) {
      // Use current locations but initial date/time filters (tickers don't depend on date selection)
      return configureUrlWithParams(`/api/v1/dar/${dashboard}/tickers`, {
        locations: filters.locations,
        dates: initialFiltersRef.current.dates, // Use initial dates (won't change)
        timeRange: initialFiltersRef.current.timeRange, // Use initial timeRange (won't change)
        widgets: [], // No sub-filters for tickers
        shouldUseEpocForTimeRange: company.shouldUseEpocForTimeRange,
        timezoneOffset,
      });
    }
    return null;
  }, [
    dashboard,
    dashboardHasTickerQuery,
    // Only depend on locations, NOT on dates - tickers are timeless
    JSON.stringify(filters.locations),
    company?.shouldUseEpocForTimeRange,
    timezoneOffset,
    enableClickhouse,
  ]);

  const { data, isLoading, mutate, error } = useSWR(
    shouldFetch ? endpoint : null,
    {
      use: [offThreadQueryResponse] as any,
      revalidateOnFocus: false,
    }
  );

  // Transform tickers to the format expected by TickerProvider
  const tickerData = useMemo(() => {
    if (!data?.tickers) return null;

    // Convert tickers object to array of widget data
    return Object.values(data.tickers)
      .filter((ticker: any) => ticker.widget_visibility === "visible" || !ticker.widget_visibility)
      .map((ticker: any) => ticker.widget)
      .filter((widget: any) => widget && widget.type); // Filter out empty widgets
  }, [data]);

  return {
    tickerData,
    isLoading,
    error,
    reload: mutate,
    // True only when ClickHouse is enabled AND this dashboard defines a ticker query
    hasTickerQuery: enableClickhouse && dashboardHasTickerQuery && endpoint !== null,
  };
};

export { useFetchTickerData };
export default useFetchTickerData;
